const STORAGE_KEY = 'vd-recorder-diagnostics-v1';
const MAX_DIAGNOSTICS = 50;
const SAFE_TOKEN = /^[a-z0-9_-]{1,64}$/;

export type RecorderDiagnosticEntry = {
  code: string;
  phase: string;
  recordedAt: string;
};

/** Keeps a bounded, privacy-safe local trail for staff support inspection. */
export function installRecorderDiagnosticBuffer(
  view: Window = window,
  storage: Pick<Storage, 'getItem' | 'setItem'> = sessionStorage,
  now: () => Date = () => new Date(),
): () => void {
  const onDiagnostic = (event: Event) => {
    const detail = (event as CustomEvent<unknown>).detail;
    if (!detail || typeof detail !== 'object') return;
    const { code, phase } = detail as { code?: unknown; phase?: unknown };
    if (typeof code !== 'string' || !SAFE_TOKEN.test(code)) return;
    if (typeof phase !== 'string' || !SAFE_TOKEN.test(phase)) return;
    try {
      const parsed = JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]') as unknown;
      const entries = Array.isArray(parsed)
        ? parsed.filter((entry): entry is RecorderDiagnosticEntry => Boolean(
          entry
          && typeof entry === 'object'
          && typeof (entry as RecorderDiagnosticEntry).code === 'string'
          && typeof (entry as RecorderDiagnosticEntry).phase === 'string'
          && typeof (entry as RecorderDiagnosticEntry).recordedAt === 'string',
        ))
        : [];
      entries.push({ code, phase, recordedAt: now().toISOString() });
      storage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_DIAGNOSTICS)));
    } catch {
      // Diagnostics must never interfere with the recorder itself.
    }
  };
  view.addEventListener('vd:recorder-diagnostic', onDiagnostic);
  return () => view.removeEventListener('vd:recorder-diagnostic', onDiagnostic);
}

export function readRecorderDiagnostics(storage: Pick<Storage, 'getItem'> = sessionStorage): unknown {
  try {
    return JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
}
